import { Dialog, Icon } from "@sarva/ui";
import { GLOBAL_SHORTCUTS, useShortcutRegistry, type Shortcut } from "./shortcuts";

type Row = { key: string; description: string; group: string };

/**
 * The discoverable half of doc 27 §9's keyboard requirement.
 *
 * It renders the globals plus whatever the current screen registered, in that
 * order — so a reviewer on the work queue sees `j`/`k`/`Enter` beside `⌘K`,
 * and a reviewer on the dashboard is not shown queue keys that do nothing.
 */
export function ShortcutSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { shortcuts } = useShortcutRegistry();
  const rows: Row[] = [
    ...GLOBAL_SHORTCUTS,
    ...shortcuts.map((s: Shortcut) => ({
      key: s.key,
      description: s.description,
      group: s.group,
    })),
  ];

  const groups = new Map<string, Row[]>();
  for (const row of rows) {
    const list = groups.get(row.group) ?? [];
    list.push(row);
    groups.set(row.group, list);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Keyboard shortcuts"
      description="The screens you live in are designed to be worked entirely from the keyboard."
    >
      <div className="flex flex-col gap-5">
        {[...groups.entries()].map(([group, items]) => (
          <section key={group}>
            <h3 className="mb-2 text-2xs font-medium uppercase text-secondary">{group}</h3>
            <ul className="flex flex-col">
              {items.map((row) => (
                <li
                  key={`${group}-${row.key}`}
                  className="flex items-center justify-between gap-4 border-b border-weak py-2 last:border-b-0"
                >
                  <span className="text-sm text-secondary">{row.description}</span>
                  <kbd className="rounded-sm border border-control bg-canvas px-1.5 py-0.5 font-mono text-xs font-medium text-default">
                    {row.key}
                  </kbd>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <p className="flex items-center gap-1.5 text-xs text-tertiary">
          <Icon name="keyboard" size={4} />
          Press ? at any time to reopen this sheet.
        </p>
      </div>
    </Dialog>
  );
}
